/**
 * Друкована форма «Бухгалтерська довідка» для ручної операції
 * (аналог друку 1С «Операция (БУ и НУ)» → Бухгалтерская справка).
 *
 * Дані рядків — з /manual-operations/{id}/print-rows (рахунки + назви субконто +
 * кількість/валюта з самого документа), тому друк доступний одразу після
 * ЗБЕРЕЖЕННЯ (кнопка «друк» у редакторі неактивна лише на новому документі).
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { manualOps } from '@/api/documents';
import { orgs } from '@/api/catalogs';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

interface JournalEntry {
  line_number: number | null;
  dt_code: string;
  cr_code: string;
  dt_subconto: string[];
  cr_subconto: string[];
  amount: string;
  tax_amount: string | null;
  dt_quantity: string | null;
  cr_quantity: string | null;
  dt_currency_amount: string | null;
  cr_currency_amount: string | null;
  dt_currency: string | null;
  cr_currency: string | null;
  content: string | null;
}

const fmt = (v: string | number | null | undefined) => {
  const n = Number(v ?? 0);
  return n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const cell: React.CSSProperties = {
  border: '1px solid #000', padding: '3pt 5pt', verticalAlign: 'top',
};

export default function ManualOperationPrintPage() {
  const { id } = useParams();

  const docQ = useQuery({
    queryKey: ['manual-op', id],
    queryFn: () => manualOps.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // Рядки з самого документа (друк доступний одразу після збереження, до
  // проведення) — назви субконто резолвить backend як у Дт-Кт журналі.
  const journalQ = useQuery({
    queryKey: ['manual-op-print-rows', id],
    queryFn: () => api.get<{ entries: JournalEntry[] }>(
      `/manual-operations/${id}/print-rows`).then((r) => r.data),
    enabled: !!id,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const doc = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === doc?.organization_id);
  if (!doc || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  }
  const entries = journalQ.data?.entries ?? [];
  const total = entries.reduce((s, e) => s + Number(e.amount || 0), 0);
  const docDate = new Date(doc.document_date).toLocaleDateString('uk-UA');
  const hasQty = entries.some((e) => e.dt_quantity || e.cr_quantity);
  const hasCur = entries.some((e) => e.dt_currency_amount || e.cr_currency_amount);
  const hasTax = entries.some((e) => e.tax_amount != null && Number(e.tax_amount) !== 0);

  const sideBlock = (
    code: string, sub: string[], qty: string | null, cur: string | null,
    curAmt: string | null,
  ) => (
    <>
      <div style={{ fontWeight: 700 }}>{code}</div>
      {sub.map((s, j) => (
        <div key={j} style={{ fontSize: '8.5pt', color: '#333' }}>{s}</div>
      ))}
      {hasQty && qty && (
        <div style={{ fontSize: '8.5pt' }}>к-сть: {Number(qty).toLocaleString('uk-UA')}</div>
      )}
      {hasCur && curAmt && (
        <div style={{ fontSize: '8.5pt' }}>{cur ?? ''} {fmt(curAmt)}</div>
      )}
    </>
  );

  return (
    <div className="print-page" style={{
      padding: '15mm', fontFamily: 'Times, serif', fontSize: '11pt',
      lineHeight: 1.4, maxWidth: '210mm', margin: '0 auto', background: 'var(--bg-panel)',
    }}>
      <PrintActions docMeta={{
        organizationId: doc.organization_id,
        documentType: 'doc.manual_operations',
        documentId: doc.id,
        filename: `Бухгалтерська довідка ${doc.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ marginBottom: '0.8rem' }}>
        <div style={{ fontWeight: 700 }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '9.5pt' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '14pt', margin: '0 0 0.3rem' }}>
        БУХГАЛТЕРСЬКА ДОВІДКА
      </h2>
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        № {doc.document_number} від {docDate}
      </div>

      {doc.operation_content && (
        <p style={{ margin: '0 0 0.8rem' }}>
          <strong>Зміст операції:</strong> {doc.operation_content}
        </p>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5pt' }}>
        <thead>
          <tr style={{ fontWeight: 700, textAlign: 'center' }}>
            <td style={{ ...cell, width: '5%' }}>№</td>
            <td style={{ ...cell, width: '24%' }}>Дебет / субконто</td>
            <td style={{ ...cell, width: '24%' }}>Кредит / субконто</td>
            <td style={{ ...cell, width: '13%' }}>Сума, грн</td>
            {hasTax && <td style={{ ...cell, width: '12%' }}>Сума (п/о), грн</td>}
            <td style={cell}>Зміст запису</td>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={i}>
              <td style={{ ...cell, textAlign: 'center' }}>{e.line_number ?? i + 1}</td>
              <td style={cell}>
                {sideBlock(e.dt_code, e.dt_subconto, e.dt_quantity, e.dt_currency, e.dt_currency_amount)}
              </td>
              <td style={cell}>
                {sideBlock(e.cr_code, e.cr_subconto, e.cr_quantity, e.cr_currency, e.cr_currency_amount)}
              </td>
              <td style={{ ...cell, textAlign: 'right' }}>{fmt(e.amount)}</td>
              {hasTax && (
                <td style={{ ...cell, textAlign: 'right' }}>
                  {e.tax_amount != null ? fmt(e.tax_amount) : ''}
                </td>
              )}
              <td style={cell}>{e.content ?? ''}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 700 }}>
            <td style={{ ...cell, textAlign: 'right' }} colSpan={3}>Разом:</td>
            <td style={{ ...cell, textAlign: 'right' }}>{fmt(total)}</td>
            {hasTax && <td style={cell}></td>}
            <td style={cell}></td>
          </tr>
        </tbody>
      </table>

      {doc.comment && (
        <p style={{ margin: '0.8rem 0 0', fontSize: '10pt' }}>
          <strong>Коментар:</strong> {doc.comment}
        </p>
      )}

      <div style={{ marginTop: '2.2rem', display: 'flex',
        justifyContent: 'space-between', fontSize: '10.5pt' }}>
        <div>
          Склав: ______________________
          <div style={{ fontSize: '8.5pt', color: '#555', paddingLeft: '4.5rem' }}>
            (посада, підпис, ПІБ)
          </div>
        </div>
        <div>
          Головний бухгалтер: ______________________
        </div>
      </div>
    </div>
  );
}
